import { create } from 'zustand'

/**
 * Lazy-activation gate for Intercom. The messenger script (~300KB frame +
 * session ping) used to initialize and boot for 100% of traffic with the
 * launcher hidden. Now nothing Intercom-related loads until `activate()` is
 * called — either by a help/feedback surface needing the messenger, or
 * automatically (on idle) for signed-in users so outbound support messages
 * still reach them.
 *
 * `activated` flips IntercomProvider's shouldInitialize in _app.tsx;
 * `booted` is set by AppProvider after it calls boot(), and is what
 * consumers must wait on before issuing messenger commands —
 * react-use-intercom drops (not queues) commands sent before boot.
 */
interface IntercomActivationState {
  activated: boolean
  booted: boolean
  activate: () => void
  setBooted: () => void
}

export const useIntercomActivationStore = create<IntercomActivationState>()(
  (set) => ({
    activated: false,
    booted: false,
    activate: () => set({ activated: true }),
    setBooted: () => set({ booted: true }),
  })
)

export const activateIntercom = () =>
  useIntercomActivationStore.getState().activate()
